/**
 * Tâche 1476 (Eric 2026-06) — « Affiche le temps d'attente estimé de chaque
 * vidéo, calculé sur les traitements passés », PARTOUT où une vidéo apparaît
 * en attente : File d'attente, Explorateur de fichiers, fiche vidéo.
 *
 * Quatre décisions qui ne vont pas de soi :
 *   1. **Un seul composant pour les trois écrans.** Trois copies du même
 *      assemblage auraient fini par annoncer trois durées différentes pour
 *      la même vidéo — et c'est précisément ce genre d'écart qui fait douter
 *      de tout le chiffre.
 *   2. **Une vidéo EN COURS dit son temps RESTANT**, pas son attente : elle
 *      n'attend plus rien, la question devient « quand est-ce que ça finit ».
 *   3. **Une estimation tirée de la TAILLE du fichier est étiquetée
 *      « approx. »** (choix d'Eric). La taille dépend du codec et du débit
 *      binaire : la présenter comme la durée réelle tromperait.
 *   4. **Sans historique, on le DIT** (acquis 1400) au lieu d'inventer une
 *      durée.
 *
 * Rendu VISIBLE à la demande d'Eric : pastille colorée bordée, et non plus un
 * fragment de texte gris noyé dans la ligne de détails.
 */
import { Hourglass, Timer } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";

/** Arrondi lisible : sous la minute on ne donne pas de chiffre du tout. */
function minutesLisibles(minutes, tr) {
  if (minutes == null) return null;
  if (minutes < 1) return tr("moins d'une minute", "less than a minute");
  if (minutes < 60) return tr(`~${Math.round(minutes)} min`, `~${Math.round(minutes)} min`);
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return tr(`~${h} h ${m} min`, `~${h} h ${m} min`);
}

export default function EtaAttente({ video, compact = false }) {
  const { tr } = useLang();
  const etat = video.queue_state;
  if (etat !== "queued" && etat !== "running") return null;

  const taille = compact ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";

  // Historique trop mince : on le dit, et on explique ce qui manque.
  if (!video.eta_suffisant) {
    return (
      <span
        className={`${taille} rounded border border-zinc-200 text-zinc-500 whitespace-nowrap inline-flex items-center gap-1`}
        data-testid={`eta-indisponible-${video.id}`}
        title={tr(
          "Aucune durée n'est inventée : il faut au moins 3 traitements mesurables (durée ou taille de fichier connue) pour estimer.",
          "No duration is invented: at least 3 measurable jobs (known duration or file size) are needed to estimate.",
        )}
      >
        <Hourglass size={11} />
        {tr("attente non estimable", "wait not estimable")}
      </span>
    );
  }

  const enCours = etat === "running";
  const minutes = enCours ? video.eta_minutes_restantes : video.eta_minutes_attente;
  const lisible = minutesLisibles(minutes, tr);

  // En file avec une estimation impossible pour CETTE vidéo (ni durée ni
  // taille connue) alors que le débit est mesuré : on ne bluffe pas.
  if (lisible == null) {
    return (
      <span
        className={`${taille} rounded border border-zinc-200 text-zinc-500 whitespace-nowrap inline-flex items-center gap-1`}
        data-testid={`eta-indisponible-${video.id}`}
        title={tr(
          "Ni la durée ni la taille de ce fichier ne sont connues : aucune estimation possible pour cette vidéo.",
          "Neither the duration nor the size of this file is known: no estimate is possible for this video.",
        )}
      >
        <Hourglass size={11} />
        {tr("attente non estimable", "wait not estimable")}
      </span>
    );
  }

  const texte = enCours
    ? (minutes < 1
      ? tr("fin imminente", "finishing shortly")
      : tr(`fin estimée dans ${lisible}`, `estimated end in ${lisible}`))
    : (minutes < 1
      ? tr("début imminent", "starting shortly")
      : tr(`début estimé dans ${lisible}`, `estimated start in ${lisible}`));

  const infobulle = tr(
    `Calculé sur les ${video.eta_echantillon_mo || video.eta_echantillon} derniers traitements, ${video.eta_ouvriers} ouvrier(s) en parallèle`
    + (video.eta_minutes_traitement != null
      ? ` · traitement complet estimé : ${minutesLisibles(video.eta_minutes_traitement, tr)}` : "")
    + (video.eta_approximatif
      ? " · estimation tirée de la TAILLE du fichier (durée inconnue), donc approximative"
      : " · estimation tirée de la durée réelle de la vidéo"),
    `Computed from the last ${video.eta_echantillon_mo || video.eta_echantillon} jobs, ${video.eta_ouvriers} worker(s) in parallel`
    + (video.eta_minutes_traitement != null
      ? ` · full processing estimated at ${minutesLisibles(video.eta_minutes_traitement, tr)}` : "")
    + (video.eta_approximatif
      ? " · estimated from the FILE SIZE (duration unknown), therefore approximate"
      : " · estimated from the actual video duration"),
  );

  return (
    <span
      className={`${taille} rounded border font-semibold whitespace-nowrap inline-flex items-center gap-1 ${
        enCours
          ? "border-teal-300 bg-teal-50 text-teal-800 dark:bg-teal-950 dark:text-teal-200"
          : "border-indigo-300 bg-indigo-50 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200"
      }`}
      data-testid={`eta-${video.id}`}
      data-eta-minutes={minutes}
      data-eta-approximatif={video.eta_approximatif ? "1" : "0"}
      title={infobulle}
    >
      {enCours ? <Timer size={11} weight="bold" /> : <Hourglass size={11} weight="bold" />}
      {texte}
      {video.eta_approximatif && (
        <span className="font-normal opacity-80" data-testid={`eta-approx-${video.id}`}>
          {tr("(approx.)", "(approx.)")}
        </span>
      )}
    </span>
  );
}
